// 1. Imports React
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// 2. Imports externes
import { useDebounce } from "use-debounce";
import {
  Building2,
  Check,
  ChevronsUpDown,
  Edit,
  Eye,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Table as TableIcon,
  ToggleLeft,
  ToggleRight,
  UserCheck,
  UserRoundCog,
  Users,
  UserPlus,
  UserX,
} from "lucide-react";

// 3. Imports UI components
import { AdminFormDialog } from "@/components/forms/AdminFormDialog";
import { EmployeeFormDialog } from "@/components/forms/EmployeeFormDialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { PaginationControls } from "@/components/ui/pagination-controls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { showErrorToast, showSuccessToast } from "@/components/ui/toast";

// 4. Imports hooks personnalisés
import { useAuth } from "@/contexts/AuthContext";
import { useCompanies } from "@/hooks/useCompanies";
import { useUpdateUser, useUsers } from "@/hooks/useUser";

// 5. Imports types
import type { Role, UserType } from "@/types/user.type";

// 6. Imports utils
import { cn, getInitials } from "@/lib/utils";

// 7. Constantes (en dehors du composant)
const TITLES = {
  SUPERUSER: "Gestion des utilisateurs",
  ADMIN: "Comptes administrateurs",
  EMPLOYEE: "Liste des employés",
};

const EMPTY_MESSAGES = {
  EMPLOYEE: { title: "Aucun employé", cta: "Ajouter un employé" },
  ADMIN: {
    title: "Aucun compte administrateur",
    cta: "Ajouter un compte admin",
  },
};

// 8. Interface
interface EmployeesListProps {
  defaultRole: Role;
}

// 9. Composant
export const EmployeesList = ({ defaultRole }: EmployeesListProps) => {
  // ========== A. Hooks React ==========
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [companySearch, setCompanySearch] = useState<string>("");
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<UserType | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<UserType | null>(
    null
  );

  // Debounce pour les recherches
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [debouncedCompanySearch] = useDebounce(companySearch, 500);

  // ========== B. Custom Hooks ==========
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperuser = user?.role === "SUPERUSER";

  // Référence pour suivre le defaultRole précédent
  const previousDefaultRoleRef = useRef<Role>(defaultRole);

  // Récupérer les entreprises uniquement pour SUPERUSER
  const { data: companiesData, isFetching: isFetchingCompanies } = useCompanies(
    {
      search: debouncedCompanySearch || undefined,
      limit: 100,
    },
    { enabled: isSuperuser }
  );

  // Réinitialiser tous les filtres quand on bascule entre Employés et Comptes
  useEffect(() => {
    // Si le defaultRole a changé, réinitialiser tous les filtres
    if (previousDefaultRoleRef.current !== defaultRole) {
      setSearchTerm("");
      setStatusFilter("all");
      setCompanyFilter("all");
      setCompanySearch("");
      setCurrentPage(1);
      previousDefaultRoleRef.current = defaultRole;
    }
  }, [defaultRole]);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, companyFilter]);

  // Paramètres de la requête avec useMemo pour optimiser les performances
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm || undefined,
      role: defaultRole || undefined,
      isActive: statusFilter === "all" ? undefined : statusFilter === "active",
      companyId: companyFilter === "all" ? undefined : companyFilter,
    }),
    [
      currentPage,
      debouncedSearchTerm,
      itemsPerPage,
      defaultRole,
      statusFilter,
      companyFilter,
    ]
  );

  // Récupérer les utilisateurs
  const {
    data: usersData,
    isLoading,
    isFetching,
    error,
  } = useUsers(queryParams);

  // Mutation pour mettre à jour un utilisateur
  const { mutate: updateUserMutation } = useUpdateUser();

  // ========== C. Early Returns ==========
  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Utilisateurs</h1>
        <div className="flex items-center justify-center h-64">
          <p className="text-red-600">
            {error?.message || "Erreur lors du chargement des utilisateurs"}
          </p>
        </div>
      </div>
    );
  }

  // ========== D. Computed Values / Helpers ==========
  const companies = companiesData?.companies || [];
  const users = usersData?.users || [];
  const totalPages = usersData?.pagination.totalPages || 0;
  const totalItems = usersData?.pagination.total || 0;

  const emptyMessage =
    defaultRole && defaultRole !== "SUPERUSER"
      ? EMPTY_MESSAGES[defaultRole]
      : { title: "Aucun utilisateur", cta: "Créer un utilisateur" };

  const tableColCount = 5 + (isSuperuser ? 1 : 0);

  // Fonction pour obtenir le nom de l'entreprise sélectionnée
  const getSelectedCompanyName = () => {
    if (companyFilter === "all") return "Toutes les entreprises";
    const company = companies.find((c) => c.id === companyFilter);
    return company?.name || "Toutes les entreprises";
  };

  // ========== E. Handlers ==========
  const handleCreateEmployee = () => {
    setEmployeeDialogOpen(true);
    setSelectedEmployee(null);
  };

  const handleCreateAdmin = () => {
    setAdminDialogOpen(true);
    setSelectedAdmin(null);
  };

  const handleEditAdmin = (user: UserType) => {
    setSelectedAdmin(user);
    setAdminDialogOpen(true);
  };

  const handleEditEmployee = (user: UserType) => {
    setSelectedEmployee(user);
    setEmployeeDialogOpen(true);
  };

  const handleToggleUserStatus = async (user: UserType) => {
    updateUserMutation(
      { id: user.id!, data: { isActive: !user.isActive } },
      {
        onSuccess: () => {
          showSuccessToast("Statut modifié avec succès");
        },
        onError: (error: Error) => {
          showErrorToast(
            error.message || "Erreur lors de la modification du statut"
          );
        },
      }
    );
  };

  // ========== F. JSX ==========
  return (
    <div className="">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="lg:ml-0">
          {defaultRole ? (
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
              {defaultRole === "ADMIN" ? (
                <UserRoundCog className="h-8 w-8 text-primary" />
              ) : (
                <Users className="h-8 w-8 text-primary" />
              )}
              {TITLES[defaultRole]}
            </h1>
          ) : (
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              Utilisateurs
            </h1>
          )}
          <p className="text-sm sm:text-base text-muted-foreground">
            {totalItems} utilisateur{totalItems > 1 ? "s" : ""} au total
          </p>
        </div>
        <Button
          className="gap-2 w-full sm:w-auto"
          onClick={() =>
            defaultRole === "ADMIN"
              ? handleCreateAdmin()
              : handleCreateEmployee()
          }
          size="sm"
        >
          <UserPlus className="h-4 w-4" />
          <span className="hidden sm:inline">
            {defaultRole === "ADMIN"
              ? "Créer un administrateur"
              : "Ajouter un employé"}
          </span>
          <span className="sm:hidden">
            {defaultRole === "ADMIN" ? "Ajouter admin" : "Ajouter employé"}
          </span>
        </Button>
      </div>
      <div className="p-4 sm:p-6">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={defaultRole === "ADMIN" ? "Rechercher par nom ou email..." : "Rechercher par nom ou PIN..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {isFetching && !isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              </div>
            )}
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value: "all" | "active" | "inactive") =>
              setStatusFilter(value)
            }
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="active">Actifs</SelectItem>
              <SelectItem value="inactive">Inactifs</SelectItem>
            </SelectContent>
          </Select>
          {isSuperuser && (
            <Popover
              open={companyPopoverOpen}
              onOpenChange={(nextOpen) => {
                setCompanyPopoverOpen(nextOpen);
                if (!nextOpen) {
                  setCompanySearch("");
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    "w-full sm:w-[250px] justify-between",
                    companyFilter === "all" && "text-muted-foreground"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span className="truncate">{getSelectedCompanyName()}</span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
              >
                <Command>
                  <CommandInput
                    placeholder="Rechercher une entreprise..."
                    value={companySearch}
                    onValueChange={setCompanySearch}
                  />
                  <CommandList>
                    {isFetchingCompanies ? (
                      <div className="py-3 text-center text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </div>
                    ) : (
                      <>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setCompanyFilter("all");
                            setCompanyPopoverOpen(false);
                            setCompanySearch("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              companyFilter === "all"
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          Toutes les entreprises
                        </CommandItem>
                        {companies.length === 0 ? (
                          <CommandEmpty>
                            Aucune entreprise trouvée.
                          </CommandEmpty>
                        ) : (
                          companies.map((company) => (
                            <CommandItem
                              key={company.id}
                              value={company.name}
                              onSelect={() => {
                                setCompanyFilter(company.id);
                                setCompanyPopoverOpen(false);
                                setCompanySearch("");
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  companyFilter === company.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              {company.name}
                            </CommandItem>
                          ))
                        )}
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* Vue mobile/tablette : Cartes */}
        {isLoading && !usersData ? (
          <div className="space-y-6 lg:hidden">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4">
                <Skeleton className="h-20 w-full" />
              </Card>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="lg:hidden text-center py-8">
            <div className="flex flex-col items-center gap-2">
              <TableIcon className="h-12 w-12 text-muted-foreground" />
              <div className="text-center">
                <h3 className="text-lg font-medium text-muted-foreground">
                  {debouncedSearchTerm
                    ? "Aucun résultat pour cette recherche"
                    : emptyMessage.title}
                </h3>
                {!debouncedSearchTerm && (
                  <Button
                    className="mt-4"
                    size="sm"
                    onClick={() =>
                      defaultRole === "ADMIN"
                        ? handleCreateAdmin()
                        : handleCreateEmployee()
                    }
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {emptyMessage.cta}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 lg:hidden">
            {users.map((user) => (
              <Card
                key={user.id}
                className="p-4 sm:p-6 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  if (defaultRole === "EMPLOYEE") {
                    navigate(`/users/${user.id}`);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarFallback className="text-xs">
                        {getInitials(user.firstName, user.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-semibold text-base truncate cursor-pointer hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (defaultRole === "EMPLOYEE") {
                            navigate(`/users/${user.id}`);
                          }
                        }}
                      >
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.firstName || user.lastName || "Utilisateur"}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {defaultRole === "EMPLOYEE" ? user.pin : user.email}
                      </p>
                      {isSuperuser && user.company?.name && (
                        <p className="text-sm text-muted-foreground truncate mt-1">
                          {user.company.name}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge
                          variant={user.isActive ? "default" : "secondary"}
                          className={
                            user.isActive
                              ? "border-0 bg-success/10 text-success hover:bg-success/20"
                              : ""
                          }
                        >
                          <span className="flex items-center gap-1">
                            {user.isActive ? (
                              <UserCheck className="h-3 w-3" />
                            ) : (
                              <UserX className="h-3 w-3" />
                            )}
                            {user.isActive ? "Actif" : "Inactif"}
                          </span>
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Créé le{" "}
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString(
                                "fr-FR"
                              )
                            : "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    {defaultRole === "ADMIN" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 sm:gap-2 border-border/80 bg-muted/50 shadow-sm hover:bg-primary hover:text-white"
                        onClick={() => handleEditAdmin(user)}
                      >
                        <Edit className="h-4 w-4" />
                        <span className="hidden sm:inline">Modifier</span>
                      </Button>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 border-border/80 bg-muted/50 p-0 shadow-sm hover:bg-accent"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {defaultRole === "EMPLOYEE" && (
                            <DropdownMenuItem
                              onClick={() => navigate(`/users/${user.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Voir détails
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleEditEmployee(user)}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleUserStatus(user)}
                          >
                            {user.isActive ? (
                              <>
                                <ToggleLeft className="mr-2 h-4 w-4" />
                                Désactiver
                              </>
                            ) : (
                              <>
                                <ToggleRight className="mr-2 h-4 w-4" />
                                Activer
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Vue desktop : Tableau */}
        <div className="animate-in fade-in-0 hidden overflow-hidden rounded-xl border border-border bg-card duration-300 lg:block">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow className="[&_th]:h-auto [&_th]:py-4">
                <TableHead>Nom</TableHead>
                <TableHead>
                  {defaultRole === "EMPLOYEE" ? "PIN" : "Email"}
                </TableHead>
                {isSuperuser && <TableHead>Entreprise</TableHead>}
                <TableHead>Statut</TableHead>
                <TableHead>Date création</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading && !usersData ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <TableRow key={i}>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-full" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                      {isSuperuser && (
                        <TableCell className="py-4">
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                      )}
                      <TableCell className="py-4">
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </TableCell>
                      <TableCell className="py-4">
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Skeleton className="h-8 w-8 rounded-md" />
                          <Skeleton className="h-8 w-8 rounded-md" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColCount} className="py-8 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <TableIcon className="h-12 w-12 text-muted-foreground" />
                      <div className="text-center">
                        <h3 className="text-lg font-medium text-muted-foreground">
                          {debouncedSearchTerm
                            ? "Aucun résultat pour cette recherche"
                            : emptyMessage.title}
                        </h3>
                        {!debouncedSearchTerm && (
                          <Button
                            className="mt-4"
                            size="sm"
                            onClick={() =>
                              defaultRole === "ADMIN"
                                ? handleCreateAdmin()
                                : handleCreateEmployee()
                            }
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {emptyMessage.cta}
                          </Button>
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const fullName =
                    user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.firstName || user.lastName || "Utilisateur";

                  return (
                    <TableRow
                      key={user.id}
                      className="transition-colors hover:bg-muted/50"
                    >
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                            <span className="font-medium text-primary">
                              {getInitials(user.firstName, user.lastName)}
                            </span>
                          </div>
                          <span
                            className={cn(
                              "font-medium text-foreground",
                              defaultRole === "EMPLOYEE" &&
                                "cursor-pointer hover:underline"
                            )}
                            onClick={() => {
                              if (defaultRole === "EMPLOYEE") {
                                navigate(`/users/${user.id}`);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (
                                defaultRole === "EMPLOYEE" &&
                                (e.key === "Enter" || e.key === " ")
                              ) {
                                e.preventDefault();
                                navigate(`/users/${user.id}`);
                              }
                            }}
                            role={
                              defaultRole === "EMPLOYEE"
                                ? "link"
                                : undefined
                            }
                            tabIndex={defaultRole === "EMPLOYEE" ? 0 : undefined}
                          >
                            {fullName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={
                          defaultRole === "EMPLOYEE"
                            ? "py-4 font-mono text-muted-foreground"
                            : "py-4 text-muted-foreground"
                        }
                      >
                        {defaultRole === "EMPLOYEE"
                          ? user.pin ?? "-"
                          : user.email ?? "-"}
                      </TableCell>
                      {isSuperuser && (
                        <TableCell className="py-4 text-muted-foreground">
                          {user.company?.name || "-"}
                        </TableCell>
                      )}

                      <TableCell className="py-4">
                        <Badge
                          variant={user.isActive ? "default" : "secondary"}
                          className={
                            user.isActive
                              ? "border-0 bg-success/10 text-success hover:bg-success/20"
                              : ""
                          }
                        >
                          <span className="flex items-center gap-1">
                            {user.isActive ? (
                              <UserCheck className="h-3 w-3" />
                            ) : (
                              <UserX className="h-3 w-3" />
                            )}
                            {user.isActive ? "Actif" : "Inactif"}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 text-muted-foreground">
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString(
                              "fr-FR"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        {defaultRole === "ADMIN" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 border-border/80 bg-muted/50 shadow-sm hover:bg-primary hover:text-primary-foreground"
                            onClick={() => handleEditAdmin(user)}
                          >
                            <Pencil className="h-4 w-4" />
                            Modifier
                          </Button>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              title="Modifier"
                              className="border-border/80 bg-muted/50 text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
                              onClick={() => handleEditEmployee(user)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              title={
                                user.isActive
                                  ? "Désactiver le compte"
                                  : "Activer le compte"
                              }
                              className={cn(
                                "border-border/80 bg-muted/50 text-muted-foreground shadow-sm",
                                user.isActive
                                  ? "hover:bg-destructive/10 hover:text-destructive"
                                  : "hover:bg-success/10 hover:text-success"
                              )}
                              onClick={() => handleToggleUserStatus(user)}
                            >
                              {user.isActive ? (
                                <UserX className="h-4 w-4" />
                              ) : (
                                <UserCheck className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              title="Voir le détail"
                              className="border-border/80 bg-muted/50 text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
                              onClick={() => navigate(`/users/${user.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {users.length > 0 && (
          <PaginationControls
            page={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setItemsPerPage(newSize);
              setCurrentPage(1);
            }}
          />
        )}
      </div>
      <EmployeeFormDialog
        open={employeeDialogOpen}
        onOpenChange={setEmployeeDialogOpen}
        onSuccess={() => {
          setEmployeeDialogOpen(false);
        }}
        employee={selectedEmployee}
      />
      <AdminFormDialog
        open={adminDialogOpen}
        onOpenChange={setAdminDialogOpen}
        onSuccess={() => {
          setAdminDialogOpen(false);
        }}
        admin={selectedAdmin}
      />
    </div>
  );
};
